import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Alert } from '../../../components/ui/Alert';
import { Input, Textarea } from '../../../components/ui/Input';
import { 
  Star, ThumbsUp, ThumbsDown, MessageSquare, 
  CheckCircle, AlertCircle, Clock, Award, Heart, Smile
} from 'lucide-react';

const RateExperience = () => {
  const [ratings, setRatings] = useState({
    overall: 0,
    responseTime: 0,
    solutionQuality: 0,
    agentHelpfulness: 0,
    systemUsability: 0
  });
  const [comment, setComment] = useState('');
  const [recommendation, setRecommendation] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const ratingCategories = [
    { 
      key: 'overall', 
      label: 'Overall Experience', 
      description: 'How would you rate your overall experience?',
      icon: <Heart className="h-4 w-4" />
    },
    { 
      key: 'responseTime', 
      label: 'Response Time', 
      description: 'How satisfied are you with the response time?',
      icon: <Clock className="h-4 w-4" />
    },
    { 
      key: 'solutionQuality', 
      label: 'Solution Quality', 
      description: 'How effective was the solution provided?',
      icon: <CheckCircle className="h-4 w-4" />
    },
    { 
      key: 'agentHelpfulness', 
      label: 'Agent Helpfulness', 
      description: 'How helpful was the support agent?',
      icon: <Smile className="h-4 w-4" />
    },
    { 
      key: 'systemUsability', 
      label: 'System Usability', 
      description: 'How easy was it to use the system?',
      icon: <Award className="h-4 w-4" />
    }
  ];

  const StarRating = ({ value, onChange, label, description, icon }) => (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <div>
          <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {label}
          </label>
          <p className="text-xs text-gray-500">{description}</p>
        </div>
      </div>
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            className={`p-1 transition-colors ${
              star <= value 
                ? 'text-yellow-400 hover:text-yellow-500' 
                : 'text-gray-300 hover:text-gray-400'
            }`}
          >
            <Star className="h-6 w-6 fill-current" />
          </button>
        ))}
        <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
          {value > 0 ? `${value}/5` : 'Not rated'}
        </span>
      </div>
    </div>
  );

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const feedback = {
        ratings,
        comment: comment.trim(),
        recommendation,
        submittedAt: new Date().toISOString(),
        ticketId: 'current-ticket' // Would come from props/context
      };

      // Mock submission - in real implementation, this would call MockDataService
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setSubmitted(true);
    } catch (err) {
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getOverallRating = () => {
    const values = Object.values(ratings).filter(v => v > 0);
    return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : 0;
  };

  const getRatingText = (rating) => {
    if (rating >= 4.5) return 'Excellent';
    if (rating >= 3.5) return 'Good';
    if (rating >= 2.5) return 'Average';
    if (rating >= 1.5) return 'Poor';
    return 'Very Poor';
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-500';
    if (rating >= 3.5) return 'text-blue-500';
    if (rating >= 2.5) return 'text-yellow-500';
    if (rating >= 1.5) return 'text-orange-500';
    return 'text-red-500';
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardContent className="text-center py-12">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Thank You for Your Feedback!
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Your feedback has been submitted successfully and will help us improve our service.
            </p>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Award className="h-5 w-5 text-yellow-500" />
                <span className="font-medium">Overall Rating</span>
              </div>
              <div className="flex items-center justify-center gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    className={`h-6 w-6 ${
                      star <= getOverallRating() 
                        ? 'text-yellow-400 fill-current' 
                        : 'text-gray-300'
                    }`}
                  />
                ))}
                <span className={`ml-2 font-medium ${getRatingColor(getOverallRating())}`}>
                  {getOverallRating().toFixed(1)}/5 - {getRatingText(getOverallRating())}
                </span>
              </div>
            </div>
            <Button onClick={() => window.location.reload()}>
              Submit Another Feedback
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          Rate Your Experience
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Help us improve by sharing your feedback about your support experience.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            {error}
          </Alert>
        )}

        {/* Rating Categories */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Star className="h-5 w-5" />
              Rate Different Aspects
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {ratingCategories.map((category) => (
              <StarRating
                key={category.key}
                value={ratings[category.key]}
                onChange={(value) => handleRatingChange(category.key, value)}
                label={category.label}
                description={category.description}
                icon={category.icon}
              />
            ))}
          </CardContent>
        </Card>

        {/* Recommendation */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ThumbsUp className="h-5 w-5" />
              Would you recommend us?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setRecommendation('yes')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  recommendation === 'yes'
                    ? 'border-green-500 bg-green-50 text-green-700 dark:bg-green-900/20'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <ThumbsUp className="h-4 w-4" />
                Yes
              </button>
              <button
                type="button"
                onClick={() => setRecommendation('no')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                  recommendation === 'no'
                    ? 'border-red-500 bg-red-50 text-red-700 dark:bg-red-900/20'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <ThumbsDown className="h-4 w-4" />
                No
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Additional Comments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Tell us more about your experience..."
              rows={4}
              className="resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">
              Optional: Share specific details about what went well or what could be improved.
            </p>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={submitting || Object.values(ratings).every(v => v === 0)}
            className="min-w-[120px]"
          >
            {submitting ? (
              <>
                <Clock className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RateExperience;
